import React, {useMemo} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import type {HotspotExplorerProps, HotspotExplorerSortMode} from "../types";
import {
    buildCountyPath,
    buildHotspotDetailPath,
    buildStatePath,
    getCountryLabel,
    getHotspotExplorerStats,
    getStateProvinceLabel,
    getVisibleHotspots,
    sortHotspots
} from "../helpers";
import {HotspotExplorerShell} from "../hotspot-explorer-shell";

export function CountyHotspotsView({hotspotExplorerIndex}: HotspotExplorerProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {countryCode = '', stateProvince = '', county = ''} = useParams();
    const sortMode = (searchParams.get('sort') as HotspotExplorerSortMode) || 'visits';
    const stateKey = `${countryCode}|${stateProvince}`;
    const countyKey = `${countryCode}|${stateProvince}|${county}`;
    const hotspots = useMemo(() => hotspotExplorerIndex.hotspotsByCountyKey.get(countyKey) ?? [], [countyKey, hotspotExplorerIndex]);
    const visibleHotspots = useMemo(
        () => sortHotspots(getVisibleHotspots(hotspots, {countryCode, stateProvince, county}), sortMode),
        [countryCode, county, hotspots, sortMode, stateProvince]
    );
    const countyGroups = hotspotExplorerIndex.countyGroupsByStateKey.get(stateKey) ?? [];

    const setSortMode = (nextSortMode: HotspotExplorerSortMode) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('sort', nextSortMode);
        setSearchParams(nextParams, {replace: true});
    }

    const openCounty = (nextCounty: string) => {
        navigate(`${buildCountyPath(countryCode, stateProvince, nextCounty)}${location.search}`);
    }

    const openHotspot = (locationId: string) => {
        navigate(buildHotspotDetailPath(locationId), {
            state: {returnTo: `${location.pathname}${location.search}`}
        });
    }

    return (
        <HotspotExplorerShell
            eyebrow="Hotspot Explorer"
            title={`${getCountryLabel(countryCode)} > ${getStateProvinceLabel(stateProvince)} > ${county}`}
            intro={`Hotspots you've visited in ${county}, ${getStateProvinceLabel(stateProvince)}.`}
            backLabel={`Back to ${getStateProvinceLabel(stateProvince)}`}
            onBack={() => navigate(`${buildStatePath(countryCode, stateProvince)}${location.search}`)}
            stats={getHotspotExplorerStats(visibleHotspots, 'Counties')}
            panelHeading="By County"
            groups={countyGroups}
            activeGroupCode={county}
            onSelectGroup={openCounty}
            hotspots={visibleHotspots}
            sortMode={sortMode}
            onChangeSort={setSortMode}
            onOpenHotspot={openHotspot}
        />
    );
}
